import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  FileText,
  Shield,
  FileSignature,
  ClipboardSignature,
  Pencil,
  Siren,
  Camera,
  Calculator,
  Eye,
  Receipt,
  FolderOpen,
  Mail,
  User,
  Car,
  Search,
  ChevronRight,
  Sparkles,
  Upload,
  ScanLine,
  AlertCircle,
  CheckCircle,
  Loader2,
  X,
  FileImage,
  Download
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import DashboardLayout from '../components/Layout/DashboardLayout';
import GuidedPhotoCapture, { PHOTO_SLOTS } from '../components/GuidedPhotoCapture';
import AddressMapPicker, { AddressMap } from '../components/AddressMapPicker';
import DamageRegionSelector from '../components/DamageRegionSelector';
import AbtretungDocument from '../components/AbtretungDocument';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NewCasePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const auftragstyp = searchParams.get('auftragstyp') || 'austausch';
  const editCaseId = searchParams.get('edit');
  const [user, setUser] = useState(null);
  const [activeStep, setActiveStep] = useState('auftrag');
  const [isDragging, setIsDragging] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [scanComplete, setScanComplete] = useState(false);
  const [scanError, setScanError] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [scanProgress, setScanProgress] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [showGuidedCapture, setShowGuidedCapture] = useState(false);
  const [showExamplePhotos, setShowExamplePhotos] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [manualEntry, setManualEntry] = useState(false);
  const [scannedFilePreview, setScannedFilePreview] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [unfallberichtPreview, setUnfallberichtPreview] = useState(null);
  const [showUnfallberichtPreview, setShowUnfallberichtPreview] = useState(false);
  const [hasUnfallbericht, setHasUnfallbericht] = useState(null);
  const [schadenortCoords, setSchadenortCoords] = useState(null);
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [fahrzeugTab, setFahrzeugTab] = useState('daten');
  const [schadenRegionen, setSchadenRegionen] = useState([]);
  const fileInputRef = useRef(null);
  const photoInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    // Kundendaten
    vorname: '',
    nachname: '',
    adresse: '',
    plzOrt: '',
    telefon: '',
    email: '',
    // Fahrzeugdaten
    fahrzeugtyp: 'PKW',
    kennzeichen: '',
    marke: '',
    modell: '',
    vin: '',
    baujahr: new Date().getFullYear().toString(),
    erstzulassung: '',
    laufleistung: '',
    vorsteuerabzugsberechtigt: 'Nein',
    // Versicherungsdaten
    versicherung: '',
    versicherungsnummer: '',
    schadennummer: '',
    schadenart: '',
    schadenhergang: '',
    schadendatum: '',
    // Gegner-Daten
    gegnerVorname: '',
    gegnerNachname: '',
    gegnerAdresse: '',
    gegnerPlzOrt: '',
    gegnerTelefon: '',
    gegnerKennzeichen: '',
    gegnerSchadentag: '',
    gegnerSchadenort: '',
    // Abtretungserklärung
    abtretungUnterschrieben: false,
    // Kalkulation
    reparaturkosten: '',
    merkantilerMinderwert: '',
    nutzungsausfall: '',
    // Rechnung
    rechnungsnummer: '',
    rechnungsdatum: '',
    zahlungsstatus: 'Offen'
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData) {
      navigate('/');
      return;
    }
    
    setUser(JSON.parse(userData));

    // Load existing case data if editing
    if (editCaseId) {
      fetch(`${API_URL}/api/cases/${editCaseId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data) {
            setFormData(prev => ({
              ...prev,
              vorname: data.vorname || '',
              nachname: data.nachname || '',
              adresse: data.adresse || '',
              plzOrt: data.plzOrt || '',
              telefon: data.telefon || '',
              email: data.email || '',
              fahrzeugtyp: data.fahrzeugtyp || 'PKW',
              kennzeichen: data.kennzeichen || '',
              marke: data.marke || '',
              modell: data.modell || '',
              vin: data.vin || '',
              baujahr: data.baujahr || new Date().getFullYear().toString(),
              erstzulassung: data.erstzulassung || '',
              laufleistung: data.laufleistung || '',
              vorsteuerabzugsberechtigt: data.vorsteuerabzugsberechtigt || 'Nein',
            }));
          }
        })
        .catch(() => {});
    }
  }, [navigate, editCaseId]);

  const steps = [
    { id: 'auftrag', label: 'Kundendaten', icon: FileText },
    { id: 'unfallbericht', label: 'Versicherung', icon: Shield },
    { id: 'fahrzeug', label: 'Fahrzeugdaten', icon: Car },
    { id: 'lichtbilder', label: 'Lichtbilder', icon: Camera },
    { id: 'abtretung', label: 'Unterschrift', icon: Pencil },
    { id: 'dokumentation', label: 'Dokumente', icon: FolderOpen },
    { id: 'uebersicht', label: 'Übersicht', icon: Eye },
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Helper function to get input class based on whether field has value
  const getInputClass = (value, additionalClasses = '') => {
    const baseClass = 'h-10 lg:h-12 border-gray-200 transition-colors rounded-none';
    const filledClass = value ? 'bg-green-50 border-green-300' : 'bg-white';
    return `${baseClass} ${filledClass} ${additionalClasses}`;
  };

  const getSelectTriggerClass = (value) => {
    const baseClass = 'h-10 lg:h-12 border-gray-200 transition-colors rounded-none';
    const filledClass = value ? 'bg-green-50 border-green-300' : 'bg-white';
    return `${baseClass} ${filledClass}`;
  };

  const handleWeiter = () => {
    const currentIndex = steps.findIndex(s => s.id === activeStep);
    if (currentIndex < steps.length - 1) {
      setActiveStep(steps[currentIndex + 1].id);
    }
  };

  const handleSaveCase = async () => {
    // Validate required fields
    if (!formData.vorname || !formData.nachname) {
      setScanError('Bitte füllen Sie mindestens Vorname und Nachname aus.');
      return;
    }

    setIsSaving(true);
    setSaveSuccess(false);
    setScanError(null);

    try {
      const token = localStorage.getItem('token');
      const url = editCaseId 
        ? `${API_URL}/api/cases/${editCaseId}`
        : `${API_URL}/api/cases`;
      const method = editCaseId ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          auftragstyp,
          ...formData
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Fehler beim Speichern');
      }

      const savedCase = await response.json();
      const caseId = editCaseId || savedCase.id;

      // Upload photos
      if (photos.length > 0) {
        for (const photo of photos) {
          try {
            const photoForm = new FormData();
            photoForm.append('file', photo.file);
            photoForm.append('photo_type', photo.type);
            await fetch(`${API_URL}/api/cases/${caseId}/photos`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}` },
              body: photoForm
            });
          } catch {}
        }
      }

      setSaveSuccess(true);
      
      // Navigate to dashboard
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);

    } catch (error) {
      console.error('Save error:', error);
      setScanError(error.message || 'Fehler beim Speichern des Falls');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

    const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleFileUpload = async (file) => {
    // Reset states
    setScanError(null);
    setScanComplete(false);
    setUploadedFile(file);
    setIsScanning(true);
    setScanProgress(0);

    // Save preview of scanned file
    if (file.type.startsWith('image/')) {
      setScannedFilePreview(URL.createObjectURL(file));
      // Also add to photos as "fahrzeugschein"
      handlePhotoUpload(file, 'fahrzeugschein');
    }

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      setScanError('Ungültiger Dateityp. Erlaubt: JPG, PNG, PDF');
      setIsScanning(false);
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setScanError('Datei zu groß. Maximum: 10MB');
      setIsScanning(false);
      return;
    }

    // Simulate initial progress
    const progressInterval = setInterval(() => {
      setScanProgress(prev => {
        if (prev >= 70) {
          clearInterval(progressInterval);
          return prev;
        }
        return prev + 10;
      });
    }, 300);

    try {
      const token = localStorage.getItem('token');
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await fetch(`${API_URL}/api/scan-vehicle-document`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataUpload
      });

      clearInterval(progressInterval);
      setScanProgress(100);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Fehler beim Scannen');
      }

      const scanResult = await response.json();
      
      if (scanResult.success) {
        // Map API response to form fields
        setFormData(prev => ({
          ...prev,
          kennzeichen: scanResult.kennzeichen || prev.kennzeichen,
          marke: scanResult.marke || prev.marke,
          modell: scanResult.modell || prev.modell,
          vin: scanResult.vin || prev.vin,
          baujahr: scanResult.baujahr || prev.baujahr,
          erstzulassung: scanResult.erstzulassung || prev.erstzulassung,
          fahrzeugtyp: scanResult.fahrzeugtyp || prev.fahrzeugtyp,
          vorname: scanResult.vorname || prev.vorname,
          nachname: scanResult.nachname || prev.nachname,
          adresse: scanResult.adresse || prev.adresse,
          plzOrt: scanResult.plzOrt || prev.plzOrt
        }));
        setScanComplete(true);
      } else {
        setScanError(scanResult.error || 'Keine Daten erkannt');
      }
    } catch (error) {
      clearInterval(progressInterval);
      console.error('Scan error:', error);
      setScanError(error.message || 'Fehler beim Verarbeiten des Dokuments');
    } finally {
      setIsScanning(false);
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setScanComplete(false);
    setScanError(null);
    setScanProgress(0);
  };

  // Demo function to show how form fields get filled
  const handleDemoScan = () => {
    setIsScanning(true);
    setScanProgress(0);
    setScanError(null);
    
    const progressInterval = setInterval(() => {
      setScanProgress(prev => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return prev;
        }
        return prev + 10;
      });
    }, 200);

    setTimeout(() => {
      clearInterval(progressInterval);
      setScanProgress(100);
      
      // Demo data - simulates successful scan result
      setFormData(prev => ({
        ...prev,
        // Kundendaten
        vorname: 'Max',
        nachname: 'Mustermann',
        adresse: 'Musterstraße 123',
        plzOrt: '50667 Köln',
        // Fahrzeugdaten
        kennzeichen: 'K-AG 2024',
        marke: 'BMW',
        modell: '320d xDrive Touring',
        vin: 'WBAPH5C55BA123456',
        baujahr: '2023',
        erstzulassung: '2023-03-15',
        fahrzeugtyp: 'PKW'
      }));
      
      setScanComplete(true);
      setIsScanning(false);
    }, 2000);
  };

  const handleGdvQuery = () => {
    if (formData.kennzeichen) {
      setIsScanning(true);
      setScanProgress(0);
      
      const progressInterval = setInterval(() => {
        setScanProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 15;
        });
      }, 150);

      setTimeout(() => {
        clearInterval(progressInterval);
        setScanProgress(100);
        // Simulate GDV response
        setFormData(prev => ({
          ...prev,
          marke: 'Volkswagen',
          modell: 'Golf 8 GTI',
          vin: 'WVWZZZ1KZMP123456',
          baujahr: '2022',
          erstzulassung: '2022-06-20'
        }));
        setIsScanning(false);
      }, 1500);
    }
  };

  // Photo upload handlers
  const handlePhotoUpload = (file, photoType) => {
    // Create preview URL
    const preview = URL.createObjectURL(file);
    
    setPhotos(prev => {
      // Remove existing photo of same type if exists
      const filtered = prev.filter(p => p.type !== photoType);
      return [...filtered, { type: photoType, file, preview }];
    });
  };

  const handlePhotoRemove = (photoType) => {
    setPhotos(prev => {
      const photo = prev.find(p => p.type === photoType);
      if (photo?.preview) {
        URL.revokeObjectURL(photo.preview);
      }
      return prev.filter(p => p.type !== photoType);
    });
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

            return (
    <DashboardLayout user={user}>
      <div className="flex flex-col lg:flex-row h-[calc(100vh-64px)] md:h-[calc(100vh-64px)]">
        {/* Left Steps Sidebar - hidden on mobile */}
        <div className="hidden lg:block w-[254px] flex-shrink-0 p-6 pr-0 overflow-visible">
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4">
          {/* Back Button */}
          <Link 
            to="/dashboard" 
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Zurück</span>
          </Link>
          
          {/* Steps Header */}
          <div className="mb-4">
            <h2 className="font-semibold text-gray-900 text-sm">Erfassen Sie die Grunddaten</h2>
            <p className="text-xs text-gray-500">für eine neue Akte</p>
          </div>
          
          {/* Steps List */}
          <nav className="flex flex-col gap-1">
            {steps.map((step, index) => {
              const isActive = activeStep === step.id;
              const isPast = steps.findIndex(s => s.id === activeStep) > index;
              
              return (
                <button
                  key={step.id}
                  onClick={() => setActiveStep(step.id)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all text-left text-[14px] leading-[20px] w-full ${
                    isActive 
                      ? 'bg-gradient-to-r from-[#29beff] to-[#5dd3ff] text-white' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    isActive ? 'border-white bg-white' : isPast ? 'border-green-500 bg-green-500' : 'border-gray-300'
                  }`}>
                    {isPast && !isActive && (
                      <div className="w-1.5 h-1.5 bg-white rounded-full" />
                    )}
                    {isActive && (
                      <div className="w-1.5 h-1.5 bg-[#29beff] rounded-full" />
                    )}
                  </div>
                  <step.icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                  <span className="font-medium text-[14px] leading-[20px]">{step.label}</span>
                </button>
              );
            })}
          </nav>
          </div>
        </div>
        
        {/* Main Content - Scrollable */}
        <div className="flex-1 min-w-0 lg:overflow-y-auto p-0 lg:p-6 lg:pl-2 pb-16 lg:pb-6" style={{ WebkitOverflowScrolling: 'touch', scrollBehavior: 'smooth' }}>
          {/* Blue Header - Dynamic based on step */}
          <div className="bg-[#29beff] lg:rounded-t-xl p-4 lg:p-5 flex items-center gap-3">
            {steps.find(s => s.id === activeStep)?.icon && 
              React.createElement(steps.find(s => s.id === activeStep).icon, {
                className: "w-5 h-5 lg:w-6 lg:h-6 text-white"
              })
            }
            <h2 className="text-lg lg:text-xl font-semibold text-white">
              {steps.find(s => s.id === activeStep)?.label || 'Auftragsformular'}
            </h2>
          </div>
          
          {/* Step Content */}
          {activeStep === 'auftrag' && (
            <>
            {/* Fahrzeugschein Scanner Section */}
          <div className="bg-white lg:border-l lg:border-r lg:border-gray-200 p-4 lg:p-6">
            {/* Scanner Header */}
            <div className="flex items-start lg:items-center gap-3 mb-4">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                scanComplete ? 'bg-green-100' : scanError ? 'bg-red-100' : 'bg-blue-100'
              }`}>
                {scanComplete ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : scanError ? (
                  <AlertCircle className="w-5 h-5 text-red-600" />
                ) : (
                  <ScanLine className="w-5 h-5 text-blue-600" />
                )}
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 text-sm lg:text-base">Fahrzeugschein Scanner</h3>
                <p className="text-xs lg:text-sm text-gray-500">Fahrzeugschein hochladen und automatisch ausfüllen lassen</p>
              </div>
            </div>
            
            {/* Scanner Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-start lg:items-center justify-between gap-2">
              <div className="flex items-start lg:items-center gap-2">
                <Sparkles className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5 lg:mt-0" />
                <p className="text-xs lg:text-sm text-blue-700">
                  <span className="font-medium text-blue-600">Automatische Erkennung:</span> Deutsche ZB1/ZB2, Österreichische und Schweizer Fahrzeugdokumente werden unterstützt
                </p>
              </div>
              <Button
                onClick={handleDemoScan}
                disabled={isScanning}
                variant="outline"
                size="sm"
                className="text-xs whitespace-nowrap border-blue-300 text-blue-600 hover:bg-blue-100"
                data-testid="demo-scan-btn"
              >
                <Sparkles className="w-3 h-3 mr-1" />
                Demo
              </Button>
            </div>
            
            {/* Error Message */}
            {scanError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-red-700">{scanError}</p>
                    <button
                      onClick={() => {
                        setScanError(null);
                        handleDemoScan();
                      }}
                      className="mt-2 text-xs text-red-600 hover:text-red-800 underline"
                    >
                      Demo-Modus verwenden →
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Demo Scan Progress (when no file uploaded) */}
            {!uploadedFile && isScanning && (
              <div className="rounded-xl p-4 mb-4 bg-blue-50 border border-blue-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-blue-100">
                    <ScanLine className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">Demo-Scan läuft...</p>
                    <p className="text-xs text-gray-500">Simuliert einen echten Fahrzeugschein-Scan</p>
                  </div>
                </div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-blue-600 font-medium flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Daten werden extrahiert...
                  </span>
                  <span className="text-xs text-blue-600">{scanProgress}%</span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-1.5">
                  <div 
                    className="bg-gradient-to-r from-[#29beff] to-[#5dd3ff] h-1.5 rounded-full transition-all duration-300"
                    style={{ width: `${scanProgress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Demo Scan Complete (when no file uploaded) */}
            {!uploadedFile && scanComplete && !isScanning && (
              <div className="rounded-xl p-4 mb-4 bg-green-50 border border-green-200">
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">Demo-Daten erfolgreich in alle Felder übertragen!</span>
                </div>
              </div>
            )}
            
            {/* Uploaded File Display */}
            {uploadedFile && (
              <div className={`rounded-xl p-4 mb-4 ${
                scanComplete ? 'bg-green-50 border border-green-200' : 'bg-blue-50 border border-blue-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      scanComplete ? 'bg-green-100' : 'bg-blue-100'
                    }`}>
                      <FileImage className={`w-5 h-5 ${scanComplete ? 'text-green-600' : 'text-blue-600'}`} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{uploadedFile.name}</p>
                      <p className="text-xs text-gray-500">
                        {(uploadedFile.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleRemoveFile}
                    className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
                
                {/* Progress Bar */}
                {isScanning && (
                  <div className="mt-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-blue-600 font-medium flex items-center gap-1">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Wird gescannt...
                      </span>
                      <span className="text-xs text-blue-600">{scanProgress}%</span>
                    </div>
                    <div className="w-full bg-blue-200 rounded-full h-1.5">
                      <div 
                        className="bg-gradient-to-r from-[#29beff] to-[#5dd3ff] h-1.5 rounded-full transition-all duration-300"
                        style={{ width: `${scanProgress}%` }}
                      />
                    </div>
                  </div>
                )}
                
                {/* Success Message */}
                {scanComplete && !isScanning && (
                  <div className="mt-3 flex items-center gap-2 text-green-600">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Daten erfolgreich extrahiert und übertragen!</span>
                  </div>
                )}
              </div>
            )}
            
            {/* Upload Area - Only show if no file uploaded */}
            {!uploadedFile && (
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-6 lg:p-10 flex flex-col items-center justify-center cursor-pointer transition-all ${
                  isDragging 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-blue-300 hover:border-blue-400 hover:bg-gray-50'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="hidden"
                  onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0])}
                />
                <Upload className="w-10 h-10 lg:w-12 lg:h-12 text-blue-400 mb-3" />
                <p className="font-semibold text-gray-700 mb-1 text-sm lg:text-base">Fahrzeugschein hochladen</p>
                <p className="text-xs lg:text-sm text-gray-500 mb-1 text-center">Klicken zum Hochladen oder Drag & Drop</p>
                <p className="text-xs text-gray-400">PDF, JPG, PNG - Max. 10MB</p>
              </div>
            )}
            
            {/* Auto-fill Notice */}
            <div className="flex items-center justify-center gap-2 text-blue-500 mt-4">
              <Sparkles className="w-4 h-4" />
              <span className="text-xs lg:text-sm">Daten werden automatisch in die Felder übertragen</span>
            </div>
          </div>

          {/* Unfallbericht Upload */}
          <div className="bg-white lg:border-l lg:border-r lg:border-gray-200 px-4 lg:px-6 py-3">
            <div
              className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                unfallberichtPreview ? 'border-green-300 bg-green-50' : 'border-gray-200 hover:border-[#29beff] hover:bg-blue-50'
              }`}
              onClick={() => {
                if (unfallberichtPreview) { setShowUnfallberichtPreview(true); return; }
                const input = document.createElement('input');
                input.type = 'file'; input.accept = 'image/*'; input.capture = 'environment';
                input.onchange = (e) => {
                  const file = e.target.files[0];
                  if (file) { setUnfallberichtPreview(URL.createObjectURL(file)); handlePhotoUpload(file, 'unfallbericht'); }
                };
                input.click();
              }}
            >
              {unfallberichtPreview ? (
                <div className="w-10 h-10 rounded border border-green-300 overflow-hidden flex-shrink-0">
                  <img src={unfallberichtPreview} alt="Unfallbericht" className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="w-10 h-10 rounded border border-dashed border-gray-300 flex items-center justify-center flex-shrink-0">
                  <Camera className="w-5 h-5 text-gray-400" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-700">Unfallbericht</p>
                <p className="text-[10px] text-gray-400">{unfallberichtPreview ? 'Hochgeladen - zum Anzeigen tippen' : 'Foto aufnehmen oder hochladen'}</p>
              </div>
              {unfallberichtPreview ? (
                <button onClick={(e) => { e.stopPropagation(); setUnfallberichtPreview(null); handlePhotoRemove('unfallbericht'); }} className="text-red-400 hover:text-red-500 p-1">
                  <X className="w-4 h-4" />
                </button>
              ) : (
                <Upload className="w-4 h-4 text-gray-400 flex-shrink-0" />
              )}
            </div>
          </div>
          
          {/* Manual entry toggle - always visible */}
          {!scanComplete && (
          <div className="bg-white lg:border-l lg:border-r lg:border-gray-200 px-4 lg:px-6 pb-4">
            <button
              onClick={() => setManualEntry(!manualEntry)}
              className="text-sm text-[#29beff] hover:text-[#1fb3f5] flex items-center gap-1"
            >
              <ChevronRight className={`w-4 h-4 transition-transform ${manualEntry ? 'rotate-90' : ''}`} />
              Daten manuell eingeben
            </button>
          </div>
          )}

          {/* Kundendaten Section - visible after scan OR manual toggle */}
          {(scanComplete || manualEntry) && (
          <div className="bg-white lg:border-l lg:border-r lg:border-gray-200 p-4 lg:p-6 pt-0">
            <div className="flex items-center justify-between pt-4 lg:pt-6 border-t border-gray-100 mb-4 lg:mb-6">
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-gray-400" />
                <h3 className="text-base lg:text-lg font-semibold text-gray-900">Kundendaten</h3>
                {scanComplete && <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">Automatisch erkannt</span>}
              </div>
              {/* Scanned file preview thumbnail */}
              {scannedFilePreview && (
                <button
                  onClick={() => setShowPreviewModal(true)}
                  className="w-16 h-12 rounded border border-gray-200 overflow-hidden flex-shrink-0 hover:border-[#29beff] hover:shadow-md transition-all"
                >
                  <img src={scannedFilePreview} alt="Fahrzeugschein" className="w-full h-full object-cover" />
                </button>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
              <div>
                <Label className="text-gray-700 mb-2 block text-sm">
                  Vorname <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={formData.vorname}
                  onChange={(e) => handleInputChange('vorname', e.target.value)}
                  className={getInputClass(formData.vorname)}
                />
              </div>
              
              <div>
                <Label className="text-gray-700 mb-2 block text-sm">
                  Nachname <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={formData.nachname}
                  onChange={(e) => handleInputChange('nachname', e.target.value)}
                  className={getInputClass(formData.nachname)}
                />
              </div>
              
              <div>
                <Label className="text-gray-700 mb-2 block text-sm">Adresse</Label>
                <Input
                  value={formData.adresse}
                                    onChange={(e) => handleInputChange('adresse', e.target.value)}
                  className={getInputClass(formData.adresse)}
                />
              </div>
              
              <div>
                <Label className="text-gray-700 mb-2 block text-sm">PLZ / Ort</Label>
                <Input
                  value={formData.plzOrt}
                  onChange={(e) => handleInputChange('plzOrt', e.target.value)}
                  placeholder="z.B. 10115 Berlin"
                  className={getInputClass(formData.plzOrt)}
                />
              </div>
              
              <div>
                <Label className="text-gray-700 mb-2 block text-sm">Telefon</Label>
                <Input
                  value={formData.telefon}
                  onChange={(e) => handleInputChange('telefon', e.target.value)}
                  className={getInputClass(formData.telefon)}
                />
              </div>
              <div>
                <Label className="text-gray-700 mb-2 block text-sm">E-Mail</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="z.B. max@beispiel.de"
                  className={getInputClass(formData.email)}
                />
              </div>
            </div>
          </div>
          )}

          {/* Fahrzeugschein Preview Modal */}
          {showPreviewModal && scannedFilePreview && (
            <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setShowPreviewModal(false)}>
              <div className="relative max-w-3xl max-h-[90vh] bg-white rounded-xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-3 border-b border-gray-200">
                  <span className="text-sm font-medium text-gray-700">Fahrzeugschein</span>
                  <button onClick={() => setShowPreviewModal(false)} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
                <div className="p-2 overflow-auto max-h-[calc(90vh-52px)]">
                  <img src={scannedFilePreview} alt="Fahrzeugschein" className="w-full object-contain" />
                </div>
              </div>
            </div>
          )}
          
          <div className="bg-white lg:border lg:border-gray-200 lg:rounded-b-xl p-4 lg:p-6">
            {/* Action Buttons */}
            <div className="hidden lg:flex justify-between">
              <Button 
                variant="outline" 
                onClick={() => navigate('/dashboard')}
                className="px-5 h-9 text-sm"
              >
                Abbrechen
              </Button>
              <Button 
                onClick={handleWeiter}
                className="bg-gradient-to-r from-[#29beff] to-[#5dd3ff] hover:from-[#1fb3f5] hover:to-[#4dc8f5] text-white text-sm gap-2 px-5 h-9 shadow-sm"
              >
                Weiter
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
          </>
          )}
          
          {/* Step 2: Unfallbericht */}
          {activeStep === 'unfallbericht' && (
            <div className="bg-white lg:border lg:border-gray-200 lg:rounded-b-xl p-4 lg:p-6">
              <div className="flex items-center gap-3 mb-6">
                <Shield className="w-5 h-5 text-gray-400" />
                <h3 className="text-lg font-semibold text-gray-900">Versicherungsdaten</h3>
              </div>

              {/* Versicherung + GDV Suche */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6 mb-6">
                <div>
                  <Label className="text-gray-700 mb-2 block text-sm">Versicherung</Label>
                  <Input value={formData.versicherung} onChange={(e) => handleInputChange('versicherung', e.target.value)} placeholder="z.B. Allianz, HUK-Coburg" className={getInputClass(formData.versicherung)} />
                </div>
                <div>
                  <Label className="text-gray-700 mb-2 block text-sm">Versicherungsnummer</Label>
                  <Input value={formData.versicherungsnummer} onChange={(e) => handleInputChange('versicherungsnummer', e.target.value)} placeholder="Versicherungsschein-Nr." className={getInputClass(formData.versicherungsnummer)} />
                </div>
                <div>
                  <Label className="text-gray-700 mb-2 block text-sm">Schadennummer</Label>
                  <Input value={formData.schadennummer} onChange={(e) => handleInputChange('schadennummer', e.target.value)} placeholder="Schadennummer der Versicherung" className={getInputClass(formData.schadennummer)} />
                </div>
                <div>
                  <Label className="text-gray-700 mb-2 block text-sm">Kennzeichen (GDV-Abfrage)</Label>
                  <div className="flex gap-2">
                    <Input
                      value={formData.gegnerKennzeichen}
                      onChange={(e) => handleInputChange('gegnerKennzeichen', e.target.value)}
                      placeholder="Gegner-Kennzeichen"
                      className={`${getInputClass(formData.gegnerKennzeichen)} flex-1`}
                    />
                    <Button
                      onClick={handleGdvQuery}
                      disabled={isScanning || !formData.gegnerKennzeichen}
                      className="h-9 lg:h-10 bg-gradient-to-r from-[#29beff] to-[#5dd3ff] hover:from-[#1fb3f5] hover:to-[#4dc8f5] text-white text-sm gap-2 px-3 shadow-sm disabled:opacity-50"
                    >
                      {isScanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                      <span className="hidden sm:inline">GDV</span>
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                    <Search className="w-3 h-3" />
                    Versicherung über Kennzeichen ermitteln
                  </p>
                </div>
              </div>

              <hr className="my-6 border-gray-100" />

              {/* Schadendetails */}
              <div className="flex items-center gap-3 mb-4">
                <Siren className="w-5 h-5 text-gray-400" />
                <h3 className="text-base font-semibold text-gray-900">Schadendetails</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6 mb-6">
                <div>
                  <Label className="text-gray-700 mb-2 block text-sm">Schadendatum</Label>
                  <Input type="date" value={formData.schadendatum} onChange={(e) => handleInputChange('schadendatum', e.target.value)} className={getInputClass(formData.schadendatum)} />
                </div>
                <div>
                  <Label className="text-gray-700 mb-2 block text-sm">Schadenart</Label>
                  <Select
                    value={formData.schadenart}
                    onValueChange={(value) => {
                      handleInputChange('schadenart', value);
                      setTimeout(async () => {
                        setIsGeneratingAI(true);
                        try {
                          const token = localStorage.getItem('token');
                          const res = await fetch(`${API_URL}/api/generate-schadenhergang`, {
                            method: 'POST',
                            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                            body: JSON.stringify({ ...formData, schadenart: value })
                          });
                          const result = await res.json();
                          if (result.success && result.text) handleInputChange('schadenhergang', result.text);
                        } catch {} finally { setIsGeneratingAI(false); }
                      }, 100);
                    }}
                  >
                    <SelectTrigger className={getSelectTriggerClass(formData.schadenart)}>
                      <SelectValue placeholder="Schadenart wählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Unfall">Unfall</SelectItem>
                      <SelectItem value="Auffahrunfall">Auffahrunfall</SelectItem>
                      <SelectItem value="Parkplatzunfall">Parkplatzunfall</SelectItem>
                      <SelectItem value="Vorfahrtsmissachtung">Vorfahrtsmissachtung</SelectItem>
                      <SelectItem value="Verkehrsunfall">Verkehrsunfall</SelectItem>
                      <SelectItem value="Unwetterschäden">Unwetterschäden</SelectItem>
                      <SelectItem value="Vandalismusschäden">Vandalismusschäden</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-700 mb-2 block text-sm">Schadenort</Label>
                  <AddressMapPicker
                    value={formData.gegnerSchadenort || ''}
                    onChange={(val) => handleInputChange('gegnerSchadenort', val)}
                    onCoordsChange={setSchadenortCoords}
                    inputClass={getInputClass(formData.gegnerSchadenort)}
                    placeholder="Adresse des Unfallortes"
                  />
                </div>
                <div>
                  <Label className="text-gray-700 mb-2 block text-sm">Karte</Label>
                  <div className="h-10 lg:h-12">
                    {schadenortCoords ? (
                      <div className="h-[160px] -mt-1">
                        <AddressMap coords={schadenortCoords} />
                      </div>
                    ) : (
                      <div className="h-10 lg:h-12 border border-gray-200 rounded-none bg-gray-50 flex items-center justify-center text-xs text-gray-400">Adresse eingeben...</div>
                    )}
                  </div>
                </div>
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-gray-700 text-sm">Schadenhergang</Label>
                    <Button type="button" variant="outline" size="sm" disabled={isGeneratingAI}
                      onClick={async () => {
                        setIsGeneratingAI(true);
                        try {
                          const token = localStorage.getItem('token');
                          const res = await fetch(`${API_URL}/api/generate-schadenhergang`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(formData) });
                          const result = await res.json();
                          if (result.success && result.text) handleInputChange('schadenhergang', result.text);
                        } catch {} finally { setIsGeneratingAI(false); }
                      }}
                      className="h-7 px-3 text-xs gap-1.5 text-[#29beff] border-[#29beff] hover:bg-blue-50" data-testid="generate-schadenhergang-btn"
                    >
                      {isGeneratingAI ? <><Loader2 className="w-3 h-3 animate-spin" /> Generiert...</> : <><Sparkles className="w-3 h-3" /> KI-Schadenhergang</>}
                    </Button>
                  </div>
                  <textarea value={formData.schadenhergang} onChange={(e) => handleInputChange('schadenhergang', e.target.value)} placeholder="Beschreiben Sie den Unfallhergang oder lassen Sie die KI generieren..." rows={6}
                    className={`w-full px-3 py-2 border rounded-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${formData.schadenhergang ? 'bg-green-50 border-green-300' : 'border-gray-200'}`}
                  />
                </div>
              </div>

              <hr className="my-6 border-gray-100" />

              {/* Unfallbericht + Gegnerdaten */}
              <div className="flex items-center gap-3 mb-4">
                <FileSignature className="w-5 h-5 text-gray-400" />
                <h3 className="text-base font-semibold text-gray-900">Unfallbericht & Gegnerdaten</h3>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <button
                  onClick={() => {
                    if (!unfallberichtPreview) {
                      const input = document.createElement('input');
                      input.type = 'file'; input.accept = 'image/*'; input.capture = 'environment';
                      input.onchange = (e) => { const file = e.target.files[0]; if (file) { setUnfallberichtPreview(URL.createObjectURL(file)); handlePhotoUpload(file, 'unfallbericht'); } };
                      input.click();
                    } else { setShowUnfallberichtPreview(true); }
                  }}
                  className={`h-12 rounded-lg border-2 flex items-center justify-center gap-2 text-sm font-medium transition-all ${unfallberichtPreview ? 'border-green-400 bg-green-50 text-green-700' : 'border-[#29beff] text-[#29beff] hover:bg-blue-50'}`}
                >
                  {unfallberichtPreview ? <><CheckCircle className="w-4 h-4" /> Hochgeladen</> : <><Camera className="w-4 h-4" /> Unfallbericht foto</>}
                </button>
                <button
                  onClick={() => setHasUnfallbericht(hasUnfallbericht === false ? null : false)}
                  className={`h-12 rounded-lg border-2 flex items-center justify-center gap-2 text-sm font-medium transition-all ${hasUnfallbericht === false ? 'border-[#29beff] bg-blue-50 text-[#29beff]' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                >
                  <User className="w-4 h-4" /> Gegnerdaten eingeben
                </button>
              </div>

              {hasUnfallbericht === false && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6 mb-4">
                  <div><Label className="text-gray-700 mb-2 block text-sm">Gegner Name</Label><Input value={formData.gegnerVorname} onChange={(e) => handleInputChange('gegnerVorname', e.target.value)} placeholder="Vor- und Nachname" className={getInputClass(formData.gegnerVorname)} /></div>
                  <div><Label className="text-gray-700 mb-2 block text-sm">Gegner Adresse</Label><Input value={formData.gegnerAdresse} onChange={(e) => handleInputChange('gegnerAdresse', e.target.value)} placeholder="Straße, PLZ Ort" className={getInputClass(formData.gegnerAdresse)} /></div>
                </div>
              )}

              {/* Navigation */}
              <div className="hidden lg:flex justify-between mt-6 pt-6 border-t border-gray-100">
                <Button variant="outline" onClick={() => setActiveStep('auftrag')} className="px-5 h-9 text-sm">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Zurück
                </Button>
                <Button onClick={handleWeiter} className="bg-gradient-to-r from-[#29beff] to-[#5dd3ff] hover:from-[#1fb3f5] hover:to-[#4dc8f5] text-white text-sm gap-2 px-5 h-9 shadow-sm">
                  Weiter <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Unfallbericht Preview Modal */}
          {showUnfallberichtPreview && unfallberichtPreview && (
            <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setShowUnfallberichtPreview(false)}>
              <div className="relative max-w-3xl max-h-[90vh] bg-white rounded-xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-3 border-b border-gray-200">
                  <span className="text-sm font-medium text-gray-700">Unfallbericht</span>
                  <button onClick={() => setShowUnfallberichtPreview(false)} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
                <div className="p-2 overflow-auto max-h-[calc(90vh-52px)]">
                  <img src={unfallberichtPreview} alt="Unfallbericht" className="w-full object-contain" />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Fahrzeug */}
          {activeStep === 'fahrzeug' && (
            <div className="bg-white lg:border lg:border-gray-200 lg:rounded-b-xl">
              {/* Tabs */}
              <div className="flex border-b border-gray-200">
                <button
                  onClick={() => setFahrzeugTab('daten')}
                  className={`flex-1 py-3 text-sm font-medium text-center transition-colors ${
                    fahrzeugTab === 'daten'
                      ? 'text-[#29beff] border-b-2 border-[#29beff]'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Car className="w-4 h-4 inline mr-2" />
                  Fahrzeugdaten
                </button>
                <button
                  onClick={() => setFahrzeugTab('schaden')}
                  className={`flex-1 py-3 text-sm font-medium text-center transition-colors ${
                    fahrzeugTab === 'schaden'
                      ? 'text-[#29beff] border-b-2 border-[#29beff]'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <AlertCircle className="w-4 h-4 inline mr-2" />
                  Schadenregion
                  {schadenRegionen.length > 0 && (
                    <span className="ml-1.5 bg-red-500 text-white text-[10px] rounded-full px-1.5 py-0.5">{schadenRegionen.length}</span>
                  )}
                </button>
              </div>

              <div className="p-4 lg:p-6">
                {/* Tab: Fahrzeugdaten */}
                {fahrzeugTab === 'daten' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
                    <div>
                      <Label className="text-gray-700 mb-2 block text-sm">
                        Fahrzeugtyp <span className="text-red-500">*</span>
                      </Label>
                      <Select 
                        value={formData.fahrzeugtyp} 
                        onValueChange={(value) => handleInputChange('fahrzeugtyp', value)}
                      >
                        <SelectTrigger className={getSelectTriggerClass(formData.fahrzeugtyp)}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="PKW">PKW</SelectItem>
                          <SelectItem value="LKW">LKW</SelectItem>
                          <SelectItem value="Motorrad">Motorrad</SelectItem>
                          <SelectItem value="Transporter">Transporter</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-gray-700 mb-2 block text-sm">Versicherung</Label>
                      <Input value={formData.versicherung} onChange={(e) => handleInputChange('versicherung', e.target.value)} placeholder="z.B. Allianz, HUK-Coburg" className={getInputClass(formData.versicherung)} />
                    </div>
                    
                    <div>
                      <Label className="text-gray-700 mb-2 block text-sm">Kennzeichen</Label>
                      <div className="flex gap-2">
                        <Input
                          value={formData.kennzeichen}
                          onChange={(e) => handleInputChange('kennzeichen', e.target.value)}
                          placeholder="z.B. K-XY 123"
                          className={`${getInputClass(formData.kennzeichen)} flex-1`}
                        />
                        <Button 
                          onClick={handleGdvQuery}
                          disabled={isScanning || !formData.kennzeichen}
                          className="h-9 lg:h-10 bg-gradient-to-r from-[#29beff] to-[#5dd3ff] hover:from-[#1fb3f5] hover:to-[#4dc8f5] text-white text-sm gap-2 px-3 shadow-sm disabled:opacity-50"
                        >
                          {isScanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                          <span className="hidden sm:inline">GDV</span>
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                        <Search className="w-3 h-3" />
                        Klicken Sie auf die Lupe für GDV-Abfrage
                      </p>
                    </div>
                    
                    <div>
                      <Label className="text-gray-700 mb-2 block text-sm">Marke</Label>
                      <Input value={formData.marke} onChange={(e) => handleInputChange('marke', e.target.value)} placeholder="z.B. BMW" className={getInputClass(formData.marke)} />
                    </div>
                    <div>
                      <Label className="text-gray-700 mb-2 block text-sm">Modell</Label>
                      <Input value={formData.modell} onChange={(e) => handleInputChange('modell', e.target.value)} placeholder="z.B. 3er (330D)" className={getInputClass(formData.modell)} />
                    </div>
                    <div>
                      <Label className="text-gray-700 mb-2 block text-sm">VIN</Label>
                      <Input value={formData.vin} onChange={(e) => handleInputChange('vin', e.target.value)} placeholder="17-stellige VIN" className={`${getInputClass(formData.vin)} font-mono text-sm`} />
                    </div>
                    <div className="grid grid-cols-2 gap-3 lg:gap-4">
                      <div>
                        <Label className="text-gray-700 mb-2 block text-sm">Baujahr</Label>
                        <Input type="number" value={formData.baujahr} onChange={(e) => handleInputChange('baujahr', e.target.value)} className={getInputClass(formData.baujahr)} />
                      </div>
                      <div>
                        <Label className="text-gray-700 mb-2 block text-sm">Erstzulassung</Label>
                        <Input type="date" value={formData.erstzulassung} onChange={(e) => handleInputChange('erstzulassung', e.target.value)} className={getInputClass(formData.erstzulassung)} />
                      </div>
                    </div>
                    <div>
                      <Label className="text-gray-700 mb-2 block text-sm">Laufleistung (km)</Label>
                      <Input value={formData.laufleistung} onChange={(e) => handleInputChange('laufleistung', e.target.value)} placeholder="z.B. 50000" className={getInputClass(formData.laufleistung)} />
                    </div>
                    <div>
                      <Label className="text-gray-700 mb-2 block text-sm">Vorsteuerabzugsberechtigt</Label>
                      <Select value={formData.vorsteuerabzugsberechtigt} onValueChange={(value) => handleInputChange('vorsteuerabzugsberechtigt', value)}>
                        <SelectTrigger className={getSelectTriggerClass(formData.vorsteuerabzugsberechtigt)}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Nein">Nein</SelectItem>
                          <SelectItem value="Ja">Ja</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {/* Tab: Schadenregion */}
                {fahrzeugTab === 'schaden' && (
                  <DamageRegionSelector
                    selectedRegions={schadenRegionen}
                    onChange={setSchadenRegionen}
                  />
                )}

                {/* Navigation */}
                <div className="hidden lg:flex justify-between mt-6 pt-6 border-t border-gray-100">
                  <Button variant="outline" onClick={() => setActiveStep('unfallbericht')} className="px-5 h-9 text-sm">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Zurück
                  </Button>
                  <Button onClick={handleWeiter} className="bg-gradient-to-r from-[#29beff] to-[#5dd3ff] hover:from-[#1fb3f5] hover:to-[#4dc8f5] text-white text-sm gap-2 px-5 h-9 shadow-sm">
                    Weiter
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
          
          {/* Step 4: Abtretungserklärung */}
          {activeStep === 'abtretung' && (
            <div className="bg-white lg:border lg:border-gray-200 lg:rounded-b-xl p-4 lg:p-6">
              <AbtretungDocument
                formData={formData}
                onSign={(sigData) => handleInputChange('abtretungUnterschrieben', true)}
              />
              
              {/* Navigation Buttons */}
              <div className="hidden lg:flex justify-between mt-6 pt-6 border-t border-gray-100">
                <Button 
                  variant="outline" 
                  onClick={() => setActiveStep('fahrzeug')}
                  className="px-5 h-9 text-sm"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Zurück
                </Button>
                <Button 
                  onClick={handleWeiter}
                  className="bg-gradient-to-r from-[#29beff] to-[#5dd3ff] hover:from-[#1fb3f5] hover:to-[#4dc8f5] text-white text-sm gap-2 px-5 h-9 shadow-sm"
                >
                  Weiter
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
          
          {/* Step 4: Lichtbilder */}
          {activeStep === 'lichtbilder' && (
            <div className="bg-white lg:border lg:border-gray-200 lg:rounded-b-xl p-4 lg:p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Camera className="w-5 h-5 text-gray-400" />
                  <h3 className="text-lg font-semibold text-gray-900">Lichtbilder / Fotos</h3>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowExamplePhotos(!showExamplePhotos)}
                  className="h-7 px-3 text-xs gap-1.5"
                  data-testid="toggle-example-photos-btn"
                >
                  <Eye className="w-3 h-3" />
                  {showExamplePhotos ? 'Beispiele ausblenden' : 'Beispielfotos anzeigen'}
                </Button>
              </div>
              
              {/* Example photos overlay */}
              {showExamplePhotos && (
                <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-blue-800 mb-3">Beispielfotos zur Orientierung:</p>
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                    {PHOTO_SLOTS.map((slot) => (
                      <div key={slot.id} className="text-center">
                        <div className="aspect-square bg-white rounded border border-blue-200 overflow-hidden mb-1">
                          <img src={slot.example} alt={slot.label} className="w-full h-full object-contain" />
                        </div>
                        <span className="text-[9px] text-blue-700">{slot.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mb-6">
                {/* Action buttons */}
                <div className="flex gap-3 mb-4">
                  <Button
                    onClick={() => setShowGuidedCapture(true)}
                    className="flex-1 h-11 bg-gradient-to-r from-[#29beff] to-[#5dd3ff] hover:from-[#1fb3f5] hover:to-[#4dc8f5] text-white text-sm gap-2 shadow-sm"
                    data-testid="start-guided-capture-btn"
                  >
                    <Camera className="w-4 h-4" />
                    Fotos aufnehmen ({photos.length}/{PHOTO_SLOTS.length})
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => photoInputRef.current?.click()}
                    className="h-11 px-4 gap-2 text-sm"
                  >
                    <Upload className="w-4 h-4" />
                    Hochladen
                  </Button>
                </div>

                {/* Upload drop zone (when no photos) */}
                {photos.length === 0 && (
                  <div
                    className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 hover:bg-blue-50 transition-colors cursor-pointer"
                    onClick={() => photoInputRef.current?.click()}
                  >
                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">Fotos hierher ziehen oder klicken zum Hochladen</p>
                    <p className="text-xs text-gray-400 mt-1">{PHOTO_SLOTS.length} Fotos empfohlen</p>
                  </div>
                )}

                {/* Uploaded photos grid - draggable */}
                {photos.length > 0 && (
                  <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-3 mb-4">
                    {photos.map((photo, index) => (
                      <div
                        key={photo.type}
                        draggable
                        onDragStart={() => setDragIndex(index)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => {
                          if (dragIndex === null || dragIndex === index) return;
                          setPhotos(prev => {
                            const arr = [...prev];
                            const [moved] = arr.splice(dragIndex, 1);
                            arr.splice(index, 0, moved);
                            return arr;
                          });
                          setDragIndex(null);
                        }}
                        onDragEnd={() => setDragIndex(null)}
                        className={`relative aspect-square rounded border-2 overflow-hidden cursor-grab active:cursor-grabbing group transition-all ${
                          dragIndex === index ? 'opacity-50 scale-95 border-blue-400' : 'border-gray-200 hover:border-blue-400'
                        }`}
                      >
                        <img src={photo.preview} alt={photo.type} className="w-full h-full object-cover" />
                        {/* Hover overlay with delete */}
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <button
                            onClick={(e) => { e.stopPropagation(); handlePhotoRemove(photo.type); }}
                            className="p-1.5 bg-red-500 rounded-full text-white hover:bg-red-600"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        {/* Label */}
                        <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] py-1 px-1.5 text-center truncate">
                          {PHOTO_SLOTS.find(s => s.id === photo.type)?.label || photo.type}
                        </div>
                        {/* Drag handle indicator */}
                        <div className="absolute top-1 left-1 bg-white/80 rounded px-1 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          <span className="text-[9px] text-gray-600">&#x2630;</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Status bar */}
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <span>{photos.length} von {PHOTO_SLOTS.length} Fotos hochgeladen</span>
                  {photos.length > 0 && (
                    <button 
                      onClick={() => setPhotos([])}
                      className="text-red-500 hover:text-red-600 text-xs"
                    >
                      Alle entfernen
                    </button>
                  )}
                </div>
                
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    const files = Array.from(e.target.files);
                    const types = PHOTO_SLOTS.map(s => s.id);
                    const usedTypes = photos.map(p => p.type);
                    const availableTypes = types.filter(t => !usedTypes.includes(t));
                    files.forEach((file, index) => {
                      if (index < availableTypes.length) {
                        handlePhotoUpload(file, availableTypes[index]);
                      }
                    });
                  }}
                />
              </div>

              {/* Guided Photo Capture Overlay */}
              {showGuidedCapture && (
                <GuidedPhotoCapture
                  photos={photos}
                  onPhotoUpload={handlePhotoUpload}
                  onPhotoRemove={handlePhotoRemove}
                  onClose={() => setShowGuidedCapture(false)}
                />
              )}
              
              {/* Navigation Buttons */}
              <div className="hidden lg:flex justify-between mt-6 pt-6 border-t border-gray-100">
                <Button 
                  variant="outline" 
                  onClick={() => setActiveStep('abtretung')}
                  className="px-5 h-9 text-sm"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Zurück
                </Button>
                <Button 
                  onClick={handleWeiter}
                  className="bg-gradient-to-r from-[#29beff] to-[#5dd3ff] hover:from-[#1fb3f5] hover:to-[#4dc8f5] text-white text-sm gap-2 px-5 h-9 shadow-sm"
                >
                  Weiter
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
          
          {/* Step 5: Übersicht */}
          {activeStep === 'uebersicht' && (
            <div className="bg-white lg:border lg:border-gray-200 lg:rounded-b-xl p-4 lg:p-6">
              <div className="flex items-center gap-3 mb-6">
                <Eye className="w-5 h-5 text-gray-400" />
                <h3 className="text-lg font-semibold text-gray-900">Fallübersicht</h3>
              </div>
              
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <User className="w-4 h-4" /> Kundendaten
                  </h4>
                  <div className="space-y-1 text-sm">
                    <p><span className="text-gray-500">Name:</span> {formData.vorname} {formData.nachname}</p>
                    <p><span className="text-gray-500">Adresse:</span> {formData.adresse}, {formData.plzOrt}</p>
                    <p><span className="text-gray-500">Kontakt:</span> {formData.telefon || formData.email || '-'}</p>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <Car className="w-4 h-4" /> Fahrzeugdaten
                  </h4>
                  <div className="space-y-1 text-sm">
                    <p><span className="text-gray-500">Fahrzeug:</span> {formData.marke} {formData.modell}</p>
                    <p><span className="text-gray-500">Kennzeichen:</span> {formData.kennzeichen}</p>
                    <p><span className="text-gray-500">VIN:</span> {formData.vin || '-'}</p>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <Shield className="w-4 h-4" /> Versicherung
                  </h4>
                  <div className="space-y-1 text-sm">
                    <p><span className="text-gray-500">Versicherung:</span> {formData.versicherung || '-'}</p>
                    <p><span className="text-gray-500">Schadennr.:</span> {formData.schadennummer || '-'}</p>
                    <p><span className="text-gray-500">Schadenart:</span> {formData.schadenart}</p>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <FileSignature className="w-4 h-4" /> Status
                  </h4>
                  <div className="space-y-1 text-sm">
                    <p><span className="text-gray-500">Abtretung:</span> {formData.abtretungUnterschrieben ? 'Unterschrieben' : 'Ausstehend'}</p>
                    <p><span className="text-gray-500">Auftragstyp:</span> {auftragstyp}</p>
                  </div>
                </div>
              </div>

              {/* Photos Thumbnails */}
              {photos.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <Camera className="w-4 h-4" /> Lichtbilder ({photos.length})
                  </h4>
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {photos.map((photo) => (
                      <div key={photo.type} className="w-16 h-16 rounded border border-gray-200 overflow-hidden flex-shrink-0">
                        <img src={photo.preview} alt={photo.type} className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Upload Button */}
              <div className="flex justify-between mt-6 pt-6 border-t border-gray-100">
                <Button 
                  variant="outline" 
                  onClick={() => setActiveStep('dokumentation')}
                  className="px-5 h-9 text-sm hidden lg:flex"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Zurück
                </Button>
                <Button 
                  onClick={handleSaveCase}
                  disabled={isSaving || saveSuccess}
                  className="bg-gradient-to-r from-emerald-500 to-emerald-400 hover:from-emerald-600 hover:to-emerald-500 text-white text-sm gap-2 px-6 h-9 shadow-sm w-full lg:w-auto"
                  data-testid="upload-case-btn"
                >
                  {isSaving ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Wird hochgeladen...</>
                  ) : saveSuccess ? (
                    <><CheckCircle className="w-4 h-4" /> Hochgeladen!</>
                  ) : (
                    <><Upload className="w-4 h-4" /> Uploaden</>
                  )}
                </Button>
              </div>

              {saveSuccess && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="font-medium text-green-800">Akte erfolgreich hochgeladen!</p>
                    <p className="text-sm text-green-600">Sie werden zur Aktenliste weitergeleitet...</p>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Step 6: Dokumente */}
          {activeStep === 'dokumentation' && (
            <div className="bg-white lg:border lg:border-gray-200 lg:rounded-b-xl p-4 lg:p-6">
              <div className="flex items-center gap-3 mb-6">
                <FolderOpen className="w-5 h-5 text-gray-400" />
                <h3 className="text-lg font-semibold text-gray-900">Dokumente</h3>
              </div>
              
              <div className="space-y-3">
                {[
                  { name: 'Fahrzeugschein', status: scannedFilePreview ? 'hochgeladen' : 'ausstehend', icon: FileText },
                  { name: 'Unfallbericht', status: unfallberichtPreview ? 'hochgeladen' : 'ausstehend', icon: Siren },
                  { name: 'Abtretungserklärung', status: formData.abtretungUnterschrieben ? 'unterschrieben' : 'ausstehend', icon: Pencil },
                  { name: 'Lichtbilder', status: photos.length > 0 ? `${photos.length} Fotos` : 'ausstehend', icon: Camera },
                  { name: 'Schadenregion', status: schadenRegionen.length > 0 ? `${schadenRegionen.length} Bereiche` : 'ausstehend', icon: Car },
                ].map((doc, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <doc.icon className="w-5 h-5 text-gray-400" />
                      <span className="font-medium text-gray-900 text-sm">{doc.name}</span>
                    </div>
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                      doc.status === 'ausstehend' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                    }`}>
                      {doc.status}
                    </span>
                  </div>
                ))}
              </div>
              
              <Button variant="outline" className="w-full gap-2 mt-6">
                <Download className="w-4 h-4" />
                Alle Dokumente als ZIP herunterladen
              </Button>
              
              {/* Navigation Buttons */}
              <div className="hidden lg:flex justify-between mt-6 pt-6 border-t border-gray-100">
                <Button 
                  variant="outline" 
                  onClick={() => setActiveStep('lichtbilder')}
                  className="px-5 h-9 text-sm"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Zurück
                </Button>
                <Button 
                  onClick={handleWeiter}
                  className="bg-gradient-to-r from-[#29beff] to-[#5dd3ff] hover:from-[#1fb3f5] hover:to-[#4dc8f5] text-white text-sm gap-2 px-5 h-9 shadow-sm"
                >
                  Weiter
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile/Tablet Wizard Bottom Navigation - fixed */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 shadow-lg">
        <div className="flex items-end justify-evenly px-1 pb-2 pt-1 relative">
          {steps.map((step, index) => {
            const isActive = activeStep === step.id;
            const isPast = steps.findIndex(s => s.id === activeStep) > index;
            const isCamera = step.id === 'lichtbilder';

            if (isCamera) {
              return (
                <button
                  key={step.id}
                  onClick={() => setActiveStep(step.id)}
                  className="flex flex-col items-center flex-1 -mt-6"
                >
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg border-4 border-white transition-colors ${
                    isActive ? 'bg-[#29beff]' : 'bg-gray-100'
                  }`}>
                    <Camera className={`w-6 h-6 ${isActive ? 'text-white' : 'text-gray-500'}`} />
                  </div>
                  <span className={`text-[8px] md:text-[10px] mt-0.5 font-medium ${isActive ? 'text-[#29beff]' : 'text-gray-400'}`}>{step.label}</span>
                </button>
              );
            }

            return (
              <button
                key={step.id}
                onClick={() => setActiveStep(step.id)}
                className={`flex flex-col items-center py-1 flex-1 transition-colors ${
                  isActive ? 'text-[#29beff]' : isPast ? 'text-green-500' : 'text-gray-400'
                }`}
              >
                <step.icon className="w-5 h-5 md:w-6 md:h-6" />
                <span className="text-[8px] md:text-[10px] mt-0.5 font-medium truncate w-full text-center">{step.label}</span>
                {isActive && <div className="w-1 h-1 rounded-full bg-[#29beff] mt-0.5" />}
              </button>
            );
          })}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default NewCasePage;
